import React, {FunctionComponent, useCallback, useLayoutEffect, useState} from "react";
import {ReduxActions, useAppDispatch, useAppSelector} from "../../redux";
import {DialogsReduxSliceStateCreateSettingsDataForGroupEntryDataGroup} from "../../redux/slices/dialogs";
import {Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, MenuItem, TextField, Typography, Unstable_Grid2 as Grid} from "@mui/material";
import {useIsMounted} from "../../../@bizkeytech";


const CreateSettingsDataForGroupDialog: FunctionComponent = () => {
    const dispatch = useAppDispatch();
    const state = useAppSelector(state => state.dialogs?.createSettingsDataForGroup);
    const [creating, setCreating] = useState(false);
    const [group, setGroup] = useState<DialogsReduxSliceStateCreateSettingsDataForGroupEntryDataGroup>();
    const isMounted = useIsMounted();

    const {hideCloseButton, disableBackdropClick, ...dialogProps} = state;

    /**
     * As soon as the dialog opens, resets the state values.
     */
    useLayoutEffect(() => {
        if (state?.open) {
            setCreating(false);
            setGroup(undefined);
        }
    }, [state?.open])

    /**
     * Closes the dialog by dispatching its relevant action to the redux store.
     */
    const closeDialog = useCallback(async (successful = false, data: DialogsReduxSliceStateCreateSettingsDataForGroupEntryDataGroup | undefined = undefined) => {
        if (state?.onClose) {
            setCreating(true);
            const resultFlag = await state.onClose(successful ? "successful" : "leave", data);
            if (!isMounted())
                return;
            setCreating(false);
            if (successful && !resultFlag)
                return;
        }
        dispatch(ReduxActions.dialogs.createSettingsDataForGroup({open: false}));
    }, [state, dispatch, isMounted])

    /**
     * Closes the dialog if the values from the state do not prevent the closing of the dialog.
     */
    const onDialogClosed = useCallback((event: Object, reason: 'backdropClick' | 'escapeKeyDown' | 'closeClick') => {
        switch (reason) {
            case "backdropClick":
                if (!state.disableBackdropClick && !creating)
                    closeDialog().then();
                break;
            case "closeClick":
                if (!state.hideCloseButton && !creating)
                    closeDialog().then();
                break;
            case "escapeKeyDown":
                if (!state.disableEscapeKeyDown && !creating)
                    closeDialog().then();
                break;
            default:
                break;
        }
    }, [creating, closeDialog, state.disableBackdropClick, state.disableEscapeKeyDown, state.hideCloseButton])

    return (
        <>
            <Dialog
                {...dialogProps}
                onClose={onDialogClosed}
            >
                <DialogTitle fontWeight={600}>
                    Choose Your Group
                </DialogTitle>
                <DialogContent>
                    <DialogContentText variant={'body1'}>
                        Please select the group you want to create an exception for <Typography component={'span'} variant={'body1'} color={'primary.main'}>
                        {state?.data?.configurationTitle ?? ""}
                    </Typography>
                    </DialogContentText>
                    <Grid container xs={12} spacing={2} marginBlockStart={2} marginBlockEnd={2}>
                        <Grid xs={12}>
                            <TextField
                                select
                                fullWidth
                                label={"Group"}
                                value={group?.id ?? ""}
                                onChange={(e) => setGroup(state?.data?.groups?.find(a => a.id === e.target.value) ?? undefined)}
                                SelectProps={{
                                    renderValue: (value: any) => state?.data?.groups?.find(e => e.id === value)?.name ?? "",
                                }}
                            >
                                {
                                    state?.data?.groups?.map(e => (
                                        <MenuItem key={e.id} value={e.id}>
                                            {e.name}
                                        </MenuItem>
                                    ))
                                }
                            </TextField>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{display: 'flex', justifyContent: 'flex-end'}}>
                    <Button
                        variant={'text'}
                        size={'medium'}
                        type={'button'}
                        color={'primary'}
                        disabled={creating}
                        onClick={() => onDialogClosed({}, 'closeClick')}
                    >
                        Cancel
                    </Button>
                    <Button
                        autoFocus
                        variant={'contained'}
                        color={'primary'}
                        size={'medium'}
                        disabled={!group || creating}
                        onClick={() => closeDialog(true, group)}
                    >
                        {
                            creating
                                ? "Creating..."
                                : "Create"
                        }
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default CreateSettingsDataForGroupDialog;
